import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth.jsx";
import { TID } from "@/constants/testIds";

const HERO_IMG = "https://images.pexels.com/photos/9259686/pexels-photo-9259686.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=940";

export default function AuthPage({ mode }) {
  const isRegister = mode === "register";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const { login, register } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = isRegister ? await register(email, password, name) : await login(email, password);
      toast.success(isRegister ? "Welcome to Hey GC" : "Welcome back");
      const next = new URLSearchParams(window.location.search).get("next");
      nav(next || (u?.role === "admin" ? "/admin" : "/dashboard"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-in failed");
    } finally { setBusy(false); }
  };

  const googleAuth = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[calc(100vh-80px)] grid grid-cols-1 lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={HERO_IMG} alt="Aerial surf" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/20 to-black/70" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <Link to="/" className="font-display text-2xl">
            Hey<span className="italic text-[color:var(--terracotta)]"> GC</span>.
          </Link>
          <div>
            <p className="font-serif italic text-2xl leading-snug max-w-md">
              "Everything worth doing on the Gold Coast — in one very well-thumbed guide."
            </p>
            <div className="mt-4 opacity-80 text-sm">— Hey GC Editors</div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <span className="editorial-divider" />
          <h1 className="font-display text-4xl mt-4 tracking-tight">
            {isRegister ? "Join Hey GC." : "Welcome back."}
          </h1>
          <p className="text-[color:var(--forest-2)] mt-2">
            {isRegister ? "Save events, plan trips, unlock the insider edit." : "Sign in to your account."}
          </p>

          <button onClick={googleAuth} data-testid={TID.authGoogleBtn} className="mt-8 btn-pill btn-outline w-full py-3">
            <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C33.6 6.1 29.1 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C33.6 6.1 29.1 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5 0 9.5-1.9 12.9-5l-6-4.9c-2 1.4-4.5 2.2-6.9 2.2-5.3 0-9.7-3.4-11.3-8L6.1 33C9.5 39.7 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.6l6 4.9C39.9 34.5 44 29.7 44 24c0-1.3-.1-2.3-.4-3.5z"/></svg>
            Continue with Google
          </button>

          <div className="my-6 flex items-center gap-3 text-xs text-[color:var(--forest-2)] uppercase tracking-widest">
            <div className="flex-1 h-px bg-[color:var(--forest-line)]" /> or <div className="flex-1 h-px bg-[color:var(--forest-line)]" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {isRegister && (
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--forest-2)]">Name</label>
                <input required data-testid={TID.authNameInput} value={name} onChange={(e) => setName(e.target.value)}
                       className="mt-2 w-full bg-white border border-[color:var(--forest-line)] rounded-full px-5 py-3 outline-none focus:border-[color:var(--terracotta)]" />
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--forest-2)]">Email</label>
              <input required type="email" data-testid={TID.authEmailInput} value={email} onChange={(e) => setEmail(e.target.value)}
                     className="mt-2 w-full bg-white border border-[color:var(--forest-line)] rounded-full px-5 py-3 outline-none focus:border-[color:var(--terracotta)]" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--forest-2)]">Password</label>
              <input required type="password" minLength={6} data-testid={TID.authPasswordInput}
                     value={password} onChange={(e) => setPassword(e.target.value)}
                     className="mt-2 w-full bg-white border border-[color:var(--forest-line)] rounded-full px-5 py-3 outline-none focus:border-[color:var(--terracotta)]" />
            </div>

            <button type="submit" disabled={busy} data-testid={TID.authSubmitBtn}
                    className="w-full btn-pill btn-primary py-4 text-base disabled:opacity-40">
              {busy ? "Please wait…" : isRegister ? "Create account" : "Sign in"}
            </button>
          </form>

          <div className="mt-6 text-sm text-[color:var(--forest-2)] text-center">
            {isRegister ? (
              <>Already have an account? <Link to="/login" data-testid={TID.authSwitchLink} className="link-editorial">Sign in</Link></>
            ) : (
              <>New to Hey GC? <Link to="/register" data-testid={TID.authSwitchLink} className="link-editorial">Create an account</Link></>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
